import { federalGeneratorStatusRank, stateGeneratorStatusRank } from "../waste";
import {
  EpaIdRequirementStatus,
  federalTierCarriesObligation,
  federalObligationTiersSqlList,
  shouldSkipThresholdComparison,
  STATES_REQUIRING_EPA_ID_FOR_VSQG,
  stateRequiresEpaIdForVsqg,
} from "./epaIdRequirementStatus";

/**
 * Exempt state tiers cannot open an upward discrepancy, even against Not a Generator.
 * A state's bottom band belongs here unless it is in STATES_REQUIRING_EPA_ID_FOR_VSQG.
 */
const EXEMPT_STATE_TIERS: Record<string, readonly string[]> = {
  // DC has no calculator of its own, so it bottoms out on the federal ladder.
  DC: ["CONDITIONALLY_EXEMPT_SMALL_QUANTITY_GENERATOR", "VERY_SMALL_QUANTITY_GENERATOR"],
  KS: ["CONDITIONALLY_EXEMPT_SMALL_QUANTITY_GENERATOR", "VERY_SMALL_QUANTITY_GENERATOR"],
  // Named SQG, but it is Maryland's sub-100 kg reduced-requirement band.
  MD: ["SMALL_QUANTITY_GENERATOR"],
  TX: ["CONDITIONALLY_EXEMPT_SMALL_QUANTITY_GENERATOR", "VERY_SMALL_QUANTITY_GENERATOR"],
  WA: ["SMALL_QUANTITY_GENERATOR"],
};

/** State registration rules can make a federal VSQG tier an actionable discrepancy. */
export function federalStatusCanExceedReported(
  status: string | null | undefined,
  facilityState: string | null | undefined
): boolean {
  return (
    federalTierCarriesObligation(status) ||
    (status === "VERY_SMALL_QUANTITY_GENERATOR" && stateRequiresEpaIdForVsqg(facilityState))
  );
}

export function isFederalGeneratorDiscrepancy(row: {
  state?: string | null;
  reportedFederalStatus?: string | null;
  calculatedFederalStatus?: string | null;
}): boolean {
  const calculated = federalGeneratorStatusRank(row.calculatedFederalStatus);
  const reported = federalGeneratorStatusRank(row.reportedFederalStatus);
  return (
    calculated < reported ||
    (calculated > reported &&
      federalStatusCanExceedReported(row.calculatedFederalStatus, row.state))
  );
}

/** Both-axis findings keep federal as primary; state-only findings use state values. */
export function isStateOnlyGeneratorDiscrepancy(row: {
  state?: string | null;
  reportedFederalStatus?: string | null;
  calculatedFederalStatus?: string | null;
  stateDirection?: string | null;
}): boolean {
  return Boolean(row.stateDirection) && !isFederalGeneratorDiscrepancy(row);
}

export function stateStatusCanExceedReported(
  status: string | null | undefined,
  facilityState: string | null | undefined
): boolean {
  return (
    stateGeneratorStatusRank(status ?? null) > 0 &&
    !(EXEMPT_STATE_TIERS[facilityState ?? ""] ?? []).includes(status ?? "")
  );
}

export type StateComparisonContext = {
  facilityState: string | null | undefined;
  requirementStatus: EpaIdRequirementStatus | null | undefined;
  reportedFederalStatus: string | null | undefined;
  calculatedFederalStatus: string | null | undefined;
  calculatedStateStatus: string | null | undefined;
};

/** States whose tier ladder does not encode the registration duty, so a tier alone is no baseline. */
const NO_BASELINE_EXCLUDED_STATES: readonly string[] = ["TX"];

/** A state obligation is actionable even when no federal tier requires registration. */
export function canCompareStateThreshold(context: StateComparisonContext): boolean {
  const tierStandsIn =
    !NO_BASELINE_EXCLUDED_STATES.includes(context.facilityState ?? "") &&
    stateStatusCanExceedReported(context.calculatedStateStatus, context.facilityState);
  return tierStandsIn || !shouldSkipThresholdComparison(context);
}

const literalList = (values: readonly string[]): string => values.map((v) => `'${v}'`).join(", ");

/** SQL callers supply trusted column expressions; rule values come only from constants above. */
export function federalStatusCanExceedReportedSql(status: string, state: string): string {
  return `COALESCE((${status} IN (${federalObligationTiersSqlList()}) OR
    (${status} = 'VERY_SMALL_QUANTITY_GENERATOR' AND
     ${state} IN (${literalList(STATES_REQUIRING_EPA_ID_FOR_VSQG)}))), FALSE)`;
}

export function stateStatusCanExceedReportedSql(
  status: string,
  state: string,
  rank: string
): string {
  const exempt = Object.entries(EXEMPT_STATE_TIERS)
    .map(([key, tiers]) => `(${state} = '${key}' AND ${status} IN (${literalList(tiers)}))`)
    .join(" OR ");
  return `(${rank} > 0 AND NOT COALESCE((${exempt}), FALSE))`;
}

export function canCompareStateThresholdSql(columns: {
  stateEligible: string;
  state: string;
  reportedFederal: string;
  calculatedFederal: string;
  requirementStatus: string;
}): string {
  const noBaselineAllowed = `COALESCE(${columns.state} NOT IN (${literalList(NO_BASELINE_EXCLUDED_STATES)}), TRUE)`;
  return `((${noBaselineAllowed} AND ${columns.stateEligible}) OR
    ${columns.reportedFederal} IS NOT NULL OR
    ${columns.requirementStatus} IS DISTINCT FROM 'NOT_REQUIRED' OR
    COALESCE(${columns.calculatedFederal} IN (${federalObligationTiersSqlList()}), FALSE))`;
}
